import { classNames, Component, h, tag } from "omi";
import isEmpty from "lodash-es/isEmpty";

import { AvatarStack } from "@/pages/project/features/directory/components/avatarStack";
import { SelectedStatus } from "@/pages/project/features/directory/components/selectButton";
import { Project, Task } from "@/api";
import { tailwind } from "@/tailwind";

import { DirectoryStore } from "../../store";
import { getUrlForProject, isActiveProject } from "../../utils";
import { ProjectStatusIndicator } from "../projectStatusIndicator";

type ProjectProps = {
  type: "project";
  project: Project;
  alwaysShowUnselect?: boolean;
};

type TaskProps = {
  type: "task";
  task: Task;
};

type Props = ProjectProps | TaskProps;

@tag("nw-directory-my-projects-list-item")
export class ListItem extends Component<Props> {
  static css = [tailwind];

  private handleSelection = (id: string, selected: boolean) => {
    DirectoryStore.updateRoadmap([id], selected);
  };

  private getStatsFromTaskMetadata(task: Task): { completions: number; avatarUrls: string[] } {
    let completions;
    let avatarUrls;

    for (const entry of task.metadata ?? []) {
      if (entry.key === "Completions") {
        completions = entry.value;
      } else if (entry.key === "CompletedBy") {
        avatarUrls = entry.value;
      }
    }

    return {
      completions: completions || 0,
      avatarUrls: avatarUrls || [],
    };
  }

  private getPartFromTaskMetadata(task: Task): number | undefined {
    for (const entry of task.metadata ?? []) {
      if (entry.key === "Part") {
        return !isEmpty(entry.value) ? parseInt(entry.value) : undefined;
      }
    }

    return undefined;
  }

  private handleCategoryClick = (event: MouseEvent, query: string | undefined) => {
    event.preventDefault();

    if (!query) return;
    DirectoryStore.search(query);
  };

  render(props: Props) {
    const id = props.type === "project" ? props.project.id : props.task.id;
    const title = props.type === "project" ? props.project.metadata?.title : props.task.name;
    const status = props.type === "project" ? props.project.status : props.task.status;
    const category = props.type === "project" ? props.project.metadata?.category : props.task.category;
    const seriesPart =
      props.type === "project" ? props.project.metadata?.part : this.getPartFromTaskMetadata(props.task);
    const isSelected = props.type === "project" ? props.project.selected : true;
    const alwaysShowUnselect = props.type === "project" ? (props.alwaysShowUnselect ?? true) : false;

    const completions =
      (props.type === "project" ? props.project.completions : this.getStatsFromTaskMetadata(props.task).completions) ||
      0;
    const avatarUrls =
      (props.type === "project" ? props.project.completedBy : this.getStatsFromTaskMetadata(props.task).avatarUrls) ??
      [];
    const progress = props.type === "project" ? props.project.progress : props.task.progress;

    return (
      <div className="flex group flex-row px-3 py-3 justify-between transition-all hover:bg-primary rounded-[20px]">
        <a className="flex flex-col space-y-1 px-3 truncate" href={getUrlForProject(id!)}>
          <h4 className="text-lg text-primary truncate hover:underline hover:underline-offset-2">{title}</h4>
          <div className="flex items-center gap-2  select-none">
            <ProjectStatusIndicator status={status} progress={progress} isActive={isActiveProject(id)} size="lg" />
            <button
              className="inline text-sm text-tertiary hover:underline hover:underline-offset-2"
              onClick={event => this.handleCategoryClick(event, category)}
            >
              {category} {seriesPart != null ? `(Part ${seriesPart})` : ""}
            </button>
            {completions > 0 && avatarUrls.length > 0 && (
              <>
                <span className="text-sm text-tertiary hidden sm:inline-block">•</span>
                <span className="hidden sm:inline-block">
                  <AvatarStack avatarUrls={avatarUrls} completionCount={completions} size="xs" />
                </span>
              </>
            )}
          </div>
        </a>
        <div
          className={classNames("flex items-center", {
            "group-hover:opacity-100 opacity-0 transition-opacity duration-300 ease-in-out": !alwaysShowUnselect,
          })}
        >
          <SelectedStatus isSelected={!!isSelected} onToggle={() => this.handleSelection(id!, !isSelected)} />
        </div>
      </div>
    );
  }
}
